/**
 * Exercise catalog for a typical chain gym (Fitness Park style): free weights, dumbbells, cables,
 * selectorized machines, plate-loaded machines, basic cardio and core.
 *
 * Ids are stable English kebab-case slugs: logged sets reference them, never rename.
 * Videos come from `videos.ts`, which only contains YouTube ids verified via oEmbed.
 */
import type { Equipment, Exercise, Muscle } from '@/domain/types'
import { EXERCISE_VIDEOS } from './videos'

export const MUSCLE_LABEL: Record<Muscle, string> = {
  chest: 'Pecho',
  back: 'Espalda',
  traps: 'Trapecio',
  front_delts: 'Deltoides anterior',
  side_delts: 'Deltoides lateral',
  rear_delts: 'Deltoides posterior',
  biceps: 'Bíceps',
  triceps: 'Tríceps',
  forearms: 'Antebrazo',
  quads: 'Cuádriceps',
  hamstrings: 'Isquiotibiales',
  glutes: 'Glúteos',
  adductors: 'Aductores',
  calves: 'Gemelos',
  abs: 'Abdomen',
  lower_back: 'Lumbar',
}

export const EQUIPMENT_LABEL: Record<Equipment, string> = {
  barbell: 'Barra',
  dumbbell: 'Mancuernas',
  machine: 'Máquina de placas',
  plate_loaded: 'Máquina de discos',
  cable: 'Polea',
  smith: 'Multipower',
  bodyweight: 'Peso corporal',
  kettlebell: 'Kettlebell',
  ez_bar: 'Barra Z',
  band: 'Banda elástica',
  cardio: 'Cardio',
}

type ExerciseDef = Omit<Exercise, 'videos'>

const def = (e: ExerciseDef): Exercise => ({ ...e, videos: EXERCISE_VIDEOS[e.id] ?? [] })

const DEFS: ExerciseDef[] = [
  /* ───────────── Chest ───────────── */
  {
    id: 'bench-press',
    name: 'Press banca con barra',
    aliases: ['Press de banca', 'Press plano', 'Banca'],
    primary: ['chest'],
    secondary: ['front_delts', 'triceps'],
    equipment: 'barbell',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2.5,
    restSec: 180,
    cues: [
      'Junta las escápulas y llévalas hacia abajo antes de desenganchar',
      'Pies firmes en el suelo y ligero arco lumbar',
      'Baja la barra controlada a la parte baja del pecho',
      'Codos a unos 45–70° del torso, no abiertos en cruz',
      'Empuja hacia arriba y ligeramente hacia atrás, sobre los hombros',
    ],
    mistakes: [
      'Rebotar la barra en el pecho',
      'Despegar los glúteos del banco',
      'Perder la retracción escapular al subir',
    ],
  },
  {
    id: 'incline-bench-press',
    name: 'Press inclinado con barra',
    aliases: ['Press banca inclinado'],
    primary: ['chest', 'front_delts'],
    secondary: ['triceps'],
    equipment: 'barbell',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2.5,
    restSec: 150,
    cues: [
      'Ajusta el banco a 30–45°',
      'Escápulas retraídas y pecho alto',
      'Baja la barra a la parte alta del pecho, bajo las clavículas',
      'Antebrazos verticales en el punto bajo',
    ],
    mistakes: [
      'Banco demasiado inclinado, se convierte en press de hombro',
      'Arquear en exceso para aplanar el ángulo',
    ],
  },
  {
    id: 'decline-bench-press',
    name: 'Press declinado con barra',
    aliases: ['Press banca declinado'],
    primary: ['chest'],
    secondary: ['triceps', 'front_delts'],
    equipment: 'barbell',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2.5,
    restSec: 150,
    cues: [
      'Sujeta bien las piernas en los rodillos',
      'Retrae las escápulas antes de desenganchar',
      'Baja la barra a la parte baja del pecho',
      'Empuja en línea recta sobre los hombros',
    ],
    mistakes: ['Bajar la barra hacia el cuello', 'Desenganchar sin ayuda con mucho peso'],
  },
  {
    id: 'close-grip-bench-press',
    name: 'Press banca agarre cerrado',
    aliases: ['Press cerrado', 'Press banca agarre estrecho'],
    primary: ['triceps', 'chest'],
    secondary: ['front_delts'],
    equipment: 'barbell',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2.5,
    restSec: 150,
    cues: [
      'Agarre a la anchura de los hombros, no más estrecho',
      'Codos pegados al cuerpo durante la bajada',
      'Toca la parte baja del pecho',
      'Bloquea los codos arriba sin perder las escápulas',
    ],
    mistakes: [
      'Agarre demasiado estrecho que carga las muñecas',
      'Abrir los codos y convertirlo en press normal',
    ],
  },
  {
    id: 'dumbbell-bench-press',
    name: 'Press banca con mancuernas',
    aliases: ['Press plano con mancuernas'],
    primary: ['chest'],
    secondary: ['front_delts', 'triceps'],
    equipment: 'dumbbell',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2,
    restSec: 120,
    cues: [
      'Sube las mancuernas apoyándolas en las rodillas al tumbarte',
      'Escápulas juntas y abajo',
      'Baja hasta notar estiramiento en el pecho',
      'Codos a unos 45° del torso',
    ],
    mistakes: [
      'Chocar las mancuernas arriba perdiendo tensión',
      'Recorrido corto por exceso de peso',
    ],
  },
  {
    id: 'incline-dumbbell-press',
    name: 'Press inclinado con mancuernas',
    aliases: ['Press inclinado mancuernas'],
    primary: ['chest', 'front_delts'],
    secondary: ['triceps'],
    equipment: 'dumbbell',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2,
    restSec: 120,
    cues: [
      'Banco a 30–45°',
      'Escápulas retraídas y pecho alto',
      'Baja las mancuernas a la altura de la parte alta del pecho',
      'Empuja juntándolas ligeramente arriba sin chocar',
    ],
    mistakes: ['Inclinación excesiva', 'Despegar la espalda del banco al empujar'],
  },
  {
    id: 'dumbbell-fly',
    name: 'Aperturas con mancuernas',
    aliases: ['Aperturas planas', 'Flyes'],
    primary: ['chest'],
    secondary: ['front_delts'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 90,
    cues: [
      'Codos ligeramente flexionados y fijos',
      'Abre en arco hasta notar estiramiento',
      'Cierra abrazando un barril',
      'Controla la bajada',
    ],
    mistakes: ['Convertirlo en un press doblando los codos', 'Bajar demasiado y forzar el hombro'],
  },
  {
    id: 'low-cable-fly',
    name: 'Aperturas en polea baja',
    aliases: ['Cruce de poleas de abajo arriba', 'Aperturas polea baja', 'Cruce de poleas bajo'],
    primary: ['chest'],
    secondary: ['front_delts'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Poleas en la posición más baja, un paso adelante',
      'Codos ligeramente flexionados y fijos',
      'Sube las manos en arco hasta la altura del pecho alto',
      'Aprieta el pecho arriba un segundo',
    ],
    mistakes: ['Tirar con los bíceps', 'Encoger los hombros al subir'],
  },
  {
    id: 'cable-crossover',
    name: 'Cruce de poleas',
    aliases: ['Cruce de poleas alto', 'Crossover', 'Aperturas en polea alta'],
    primary: ['chest'],
    secondary: ['front_delts'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Poleas a la altura de los hombros o algo más altas',
      'Torso ligeramente inclinado hacia delante',
      'Cierra en arco hasta juntar las manos delante del pecho',
      'Vuelve despacio hasta notar estiramiento',
    ],
    mistakes: ['Doblar y estirar los codos en cada repetición', 'Usar impulso con el cuerpo'],
  },
  {
    id: 'pec-deck',
    name: 'Contractor de pecho (pec-deck)',
    aliases: ['Pec deck', 'Peck deck', 'Mariposa', 'Aperturas en máquina'],
    primary: ['chest'],
    secondary: ['front_delts'],
    equipment: 'machine',
    pattern: 'isolation_upper',
    compound: false,
    increment: 5,
    restSec: 90,
    cues: [
      'Ajusta el asiento para que las manos queden a la altura del pecho',
      'Espalda pegada al respaldo y pecho alto',
      'Cierra en arco con los codos ligeramente flexionados',
      'Controla la vuelta hasta notar estiramiento',
    ],
    mistakes: ['Asiento demasiado bajo que carga el hombro', 'Soltar la vuelta sin control'],
  },
  {
    id: 'machine-chest-press',
    name: 'Press de pecho en máquina',
    aliases: ['Press pecho máquina', 'Chest press'],
    primary: ['chest'],
    secondary: ['front_delts', 'triceps'],
    equipment: 'machine',
    pattern: 'horizontal_push',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Ajusta el asiento: agarres a la altura del pecho medio',
      'Escápulas juntas contra el respaldo',
      'Empuja sin bloquear los codos de golpe',
      'Vuelve controlando hasta notar estiramiento',
    ],
    mistakes: ['Despegar la espalda del respaldo', 'Asiento mal ajustado'],
  },
  {
    id: 'plate-loaded-chest-press',
    name: 'Press de pecho en máquina de discos',
    aliases: ['Press pecho Hammer', 'Hammer Strength press plano', 'Press isolateral'],
    primary: ['chest'],
    secondary: ['front_delts', 'triceps'],
    equipment: 'plate_loaded',
    pattern: 'horizontal_push',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Agarres a la altura de la parte media del pecho',
      'Escápulas retraídas y pies firmes',
      'Empuja en el arco de la máquina sin despegar la espalda',
      'Controla la bajada hasta el estiramiento',
    ],
    mistakes: ['Recorrido corto por exceso de discos', 'Encoger los hombros al empujar'],
  },
  {
    id: 'plate-loaded-incline-press',
    name: 'Press inclinado en máquina de discos',
    aliases: ['Press inclinado Hammer', 'Press inclinado isolateral'],
    primary: ['chest', 'front_delts'],
    secondary: ['triceps'],
    equipment: 'plate_loaded',
    pattern: 'horizontal_push',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Asiento con los agarres a la altura de la parte alta del pecho',
      'Pecho alto y escápulas juntas',
      'Empuja hacia arriba y adelante siguiendo la máquina',
      'Baja controlado hasta notar estiramiento',
    ],
    mistakes: ['Asiento demasiado alto que carga el hombro', 'Rebotar abajo'],
  },
  {
    id: 'smith-incline-press',
    name: 'Press inclinado en multipower',
    aliases: ['Press inclinado Smith', 'Press inclinado en máquina Smith'],
    primary: ['chest', 'front_delts'],
    secondary: ['triceps'],
    equipment: 'smith',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2.5,
    restSec: 120,
    cues: [
      'Coloca el banco para que la barra caiga en la parte alta del pecho',
      'Banco a 30–45°',
      'Escápulas retraídas durante toda la serie',
      'Controla la bajada y empuja sin rebotar',
    ],
    mistakes: [
      'Banco mal colocado: la barra cae en el cuello o el abdomen',
      'Bloquear los codos con golpe',
    ],
    trend: {
      by: 'Jeff Nippard',
      why: 'La guía fija quita estabilidad que gestionar: puedes apurar cerca del fallo y cargar el pecho alto con seguridad y sin compañero.',
    },
  },
  {
    id: 'push-up',
    name: 'Flexiones',
    aliases: ['Flexiones de pecho', 'Push-ups', 'Lagartijas'],
    primary: ['chest'],
    secondary: ['triceps', 'front_delts', 'abs'],
    equipment: 'bodyweight',
    pattern: 'horizontal_push',
    compound: true,
    increment: 2.5,
    bodyweight: true,
    restSec: 90,
    cues: [
      'Manos algo más abiertas que los hombros',
      'Cuerpo en línea recta: glúteos y abdomen apretados',
      'Baja hasta casi tocar el suelo con el pecho',
      'Codos a unos 45° del torso',
    ],
    mistakes: ['Dejar caer la cadera', 'Recorrido a medias'],
  },
  {
    id: 'dips',
    name: 'Fondos en paralelas',
    aliases: ['Fondos lastrados', 'Fondos', 'Dips'],
    primary: ['chest', 'triceps'],
    secondary: ['front_delts'],
    equipment: 'bodyweight',
    pattern: 'vertical_push',
    compound: true,
    increment: 2.5,
    bodyweight: true,
    restSec: 120,
    cues: [
      'Hombros abajo, lejos de las orejas',
      'Inclina el torso adelante para más pecho',
      'Baja hasta que los hombros queden a la altura de los codos',
      'Empuja hasta bloquear sin balancearte',
    ],
    mistakes: [
      'Bajar demasiado y forzar el hombro',
      'Encoger los hombros',
      'Balancear las piernas',
    ],
  },
  {
    id: 'machine-dip',
    name: 'Fondos en máquina',
    aliases: ['Fondos sentado', 'Dip machine'],
    primary: ['triceps'],
    secondary: ['chest', 'front_delts'],
    equipment: 'machine',
    pattern: 'vertical_push',
    compound: true,
    increment: 5,
    restSec: 90,
    cues: [
      'Espalda pegada al respaldo',
      'Codos cerca del cuerpo',
      'Empuja hasta extender los codos por completo',
      'Sube controlado hasta notar estiramiento',
    ],
    mistakes: ['Encoger los hombros', 'Recorrido corto'],
  },

  /* ───────────── Back ───────────── */
  {
    id: 'deadlift',
    name: 'Peso muerto convencional',
    aliases: ['Peso muerto', 'Deadlift'],
    primary: ['hamstrings', 'glutes', 'lower_back'],
    secondary: ['back', 'traps', 'quads', 'forearms'],
    equipment: 'barbell',
    pattern: 'hinge',
    compound: true,
    increment: 2.5,
    restSec: 180,
    cues: [
      'Barra sobre la mitad del pie, pegada a las espinillas',
      'Espalda neutra y pecho alto antes de tirar',
      'Tensa los dorsales: "dobla" la barra',
      'Empuja el suelo con las piernas y extiende la cadera a la vez',
      'Baja la barra pegada al cuerpo',
    ],
    mistakes: [
      'Redondear la zona lumbar',
      'Subir la cadera antes que la barra',
      'Hiperextender la espalda arriba',
    ],
  },
  {
    id: 'sumo-deadlift',
    name: 'Peso muerto sumo',
    aliases: ['Sumo'],
    primary: ['glutes', 'quads', 'hamstrings'],
    secondary: ['adductors', 'lower_back', 'back', 'traps'],
    equipment: 'barbell',
    pattern: 'hinge',
    compound: true,
    increment: 2.5,
    restSec: 180,
    cues: [
      'Pies abiertos y puntas hacia fuera',
      'Rodillas en la dirección de los pies',
      'Agarre dentro de las piernas, brazos verticales',
      'Torso más vertical y empuja el suelo hacia fuera',
    ],
    mistakes: ['Rodillas hacia dentro', 'Cadera demasiado baja al empezar'],
  },
  {
    id: 'pendlay-row',
    name: 'Remo Pendlay',
    aliases: ['Remo desde el suelo'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps', 'lower_back'],
    equipment: 'barbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2.5,
    restSec: 150,
    cues: [
      'Torso casi paralelo al suelo y espalda neutra',
      'Cada repetición empieza con la barra parada en el suelo',
      'Tira explosivo hacia la parte baja del pecho',
      'No subas el torso para ayudarte',
    ],
    mistakes: ['Levantar el torso en cada repetición', 'Redondear la espalda'],
  },
  {
    id: 'barbell-row',
    name: 'Remo con barra',
    aliases: ['Remo inclinado con barra', 'Remo con barra prono'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps', 'lower_back'],
    equipment: 'barbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2.5,
    restSec: 120,
    cues: [
      'Bisagra de cadera con torso a unos 45° o más inclinado',
      'Espalda neutra y abdomen tenso',
      'Tira llevando los codos atrás hacia la cadera',
      'Baja controlado sin perder la posición',
    ],
    mistakes: ['Usar impulso con la cadera', 'Encoger los hombros en vez de remar'],
  },
  {
    id: 't-bar-row',
    name: 'Remo en T',
    aliases: ['Remo T', 'T-bar row', 'Remo en punta'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps', 'lower_back'],
    equipment: 'plate_loaded',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Rodillas algo flexionadas y espalda neutra',
      'Pecho apoyado si la máquina lo permite',
      'Tira los codos atrás y junta las escápulas',
      'Estira los brazos del todo al bajar',
    ],
    mistakes: ['Tirar con la zona lumbar', 'Recorrido corto por exceso de discos'],
  },
  {
    id: 'one-arm-dumbbell-row',
    name: 'Remo con mancuerna a una mano',
    aliases: ['Remo mancuerna un brazo', 'Remo unilateral'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps'],
    equipment: 'dumbbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Apoya mano y rodilla en el banco, espalda plana',
      'Tira la mancuerna hacia la cadera, no al hombro',
      'Codo pegado al cuerpo',
      'Estira el brazo del todo abajo',
    ],
    mistakes: ['Girar el torso para subir el peso', 'Tirar hacia el pecho con el bíceps'],
  },
  {
    id: 'seated-cable-row',
    name: 'Remo en polea baja',
    aliases: ['Remo sentado en polea', 'Remo polea sentado', 'Remo gironda', 'Remo con triángulo'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'cable',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Rodillas algo flexionadas y torso erguido',
      'Tira el agarre hacia el abdomen',
      'Junta las escápulas al final',
      'Deja que los hombros vayan adelante al estirar',
    ],
    mistakes: ['Balancear el torso adelante y atrás', 'Encoger los hombros'],
  },
  {
    id: 'pull-up',
    name: 'Dominadas',
    aliases: ['Dominadas lastradas', 'Dominadas pronas', 'Pull-ups'],
    primary: ['back'],
    secondary: ['biceps', 'rear_delts', 'forearms'],
    equipment: 'bodyweight',
    pattern: 'vertical_pull',
    compound: true,
    increment: 2.5,
    bodyweight: true,
    restSec: 150,
    cues: [
      'Agarre prono algo más ancho que los hombros',
      'Empieza bajando las escápulas',
      'Sube llevando los codos hacia las costillas',
      'Barbilla por encima de la barra',
      'Baja hasta extender los brazos por completo',
    ],
    mistakes: ['Medias repeticiones', 'Balancear el cuerpo (kipping)'],
  },
  {
    id: 'chin-up',
    name: 'Dominadas supinas',
    aliases: ['Dominadas agarre supino', 'Chin-ups'],
    primary: ['back', 'biceps'],
    secondary: ['rear_delts', 'forearms'],
    equipment: 'bodyweight',
    pattern: 'vertical_pull',
    compound: true,
    increment: 2.5,
    bodyweight: true,
    restSec: 150,
    cues: [
      'Agarre supino a la anchura de los hombros',
      'Baja las escápulas antes de tirar',
      'Lleva el pecho hacia la barra',
      'Baja controlado hasta brazos extendidos',
    ],
    mistakes: ['Recorrido incompleto', 'Usar impulso de piernas'],
  },
  {
    id: 'assisted-pull-up-machine',
    name: 'Dominadas en máquina asistida',
    aliases: ['Dominadas asistidas', 'Máquina de dominadas asistidas'],
    primary: ['back'],
    secondary: ['biceps', 'rear_delts'],
    equipment: 'machine',
    pattern: 'vertical_pull',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'El peso de la pila es asistencia: menos peso, más difícil',
      'Rodillas firmes en la plataforma',
      'Baja las escápulas y tira los codos hacia las costillas',
      'Baja controlado hasta brazos extendidos',
    ],
    mistakes: ['Usar tanta asistencia que no cuesta', 'Recorrido incompleto'],
  },
  {
    id: 'lat-pulldown',
    name: 'Jalón al pecho',
    aliases: ['Jalón', 'Jalón polea alta', 'Jalón agarre ancho'],
    primary: ['back'],
    secondary: ['biceps', 'rear_delts'],
    equipment: 'cable',
    pattern: 'vertical_pull',
    compound: true,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Ajusta los rodillos para fijar los muslos',
      'Pecho alto y ligera inclinación atrás',
      'Tira la barra a la parte alta del pecho',
      'Sube controlado hasta estirar los dorsales',
    ],
    mistakes: ['Echarse atrás para usar impulso', 'Bajar la barra por detrás de la nuca'],
  },
  {
    id: 'close-grip-pulldown',
    name: 'Jalón agarre estrecho',
    aliases: ['Jalón con triángulo', 'Jalón agarre neutro'],
    primary: ['back'],
    secondary: ['biceps', 'rear_delts'],
    equipment: 'cable',
    pattern: 'vertical_pull',
    compound: true,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Usa el triángulo o agarre neutro estrecho',
      'Pecho alto hacia el agarre',
      'Lleva los codos abajo y atrás',
      'Estira del todo arriba',
    ],
    mistakes: ['Tirar con los brazos y no con la espalda', 'Balancear el torso'],
  },
  {
    id: 'machine-pulldown',
    name: 'Jalón en máquina',
    aliases: ['Dominadas máquina', 'Jalón Hammer', 'Jalón isolateral'],
    primary: ['back'],
    secondary: ['biceps', 'rear_delts'],
    equipment: 'plate_loaded',
    pattern: 'vertical_pull',
    compound: true,
    increment: 5,
    restSec: 90,
    cues: [
      'Ajusta el asiento para fijar bien los muslos',
      'Pecho alto y escápulas abajo al empezar',
      'Tira los codos hacia las costillas',
      'Sube controlado hasta estirar del todo',
    ],
    mistakes: ['Encoger los hombros arriba sin control', 'Recorrido corto'],
  },
  {
    id: 'cable-serratus',
    name: 'Serrato en polea',
    aliases: ['Pullover en polea', 'Jalón con brazos rectos', 'Straight-arm pulldown'],
    primary: ['back'],
    secondary: ['triceps'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea alta con barra o cuerda, torso inclinado adelante',
      'Codos casi rectos y fijos',
      'Lleva el agarre hacia los muslos en arco',
      'Sube despacio hasta notar estiramiento en el dorsal',
    ],
    mistakes: ['Doblar los codos y convertirlo en un press', 'Mover el torso para ayudarte'],
  },
  {
    id: 'plate-loaded-row',
    name: 'Remo en máquina de discos',
    aliases: ['Remo Hammer', 'Remo isolateral'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'plate_loaded',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 5,
    restSec: 90,
    cues: [
      'Pecho apoyado en el soporte',
      'Tira los codos atrás junto al cuerpo',
      'Junta las escápulas al final',
      'Estira los brazos del todo al volver',
    ],
    mistakes: ['Despegar el pecho del apoyo', 'Encoger los hombros'],
  },
  {
    id: 'machine-row',
    name: 'Remo en máquina',
    aliases: ['Remo sentado en máquina', 'Remo con apoyo de pecho'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'machine',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 5,
    restSec: 90,
    cues: [
      'Ajusta el asiento: agarres a la altura del pecho bajo',
      'Pecho pegado al apoyo',
      'Tira los codos atrás y junta las escápulas',
      'Vuelve controlado estirando la espalda',
    ],
    mistakes: ['Despegar el pecho del apoyo', 'Tirones con impulso'],
  },
  {
    id: 'chest-supported-dumbbell-row',
    name: 'Remo con mancuernas en banco inclinado',
    aliases: ['Remo pecho apoyado', 'Remo en banco inclinado'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'dumbbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Banco a 30–45°, pecho apoyado',
      'Tira los codos atrás hacia la cadera',
      'Junta las escápulas arriba',
      'Estira del todo abajo',
    ],
    mistakes: ['Levantar el pecho del banco', 'Encoger los hombros'],
  },
  {
    id: 'plate-loaded-high-row',
    name: 'Remo alto en máquina de discos',
    aliases: ['High row Hammer', 'Remo alto isolateral'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'plate_loaded',
    pattern: 'vertical_pull',
    compound: true,
    increment: 5,
    restSec: 90,
    cues: [
      'Fija bien los muslos bajo el rodillo',
      'Tira de arriba abajo y atrás hacia la cadera',
      'Codos pegados al cuerpo',
      'Estira del todo al volver',
    ],
    mistakes: ['Echar el torso atrás', 'Recorrido corto'],
  },
  {
    id: 'dumbbell-pullover',
    name: 'Pullover con mancuerna',
    aliases: ['Pullover'],
    primary: ['back', 'chest'],
    secondary: ['triceps'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 90,
    cues: [
      'Sujeta la mancuerna con las dos manos sobre el pecho',
      'Codos ligeramente flexionados y fijos',
      'Baja por detrás de la cabeza hasta notar estiramiento',
      'Vuelve en arco hasta encima del pecho',
    ],
    mistakes: ['Doblar los codos en cada repetición', 'Arquear la zona lumbar al bajar'],
  },
  {
    id: 'back-extension',
    name: 'Hiperextensiones',
    aliases: ['Extensiones lumbares', 'Banco romano', 'Hiperextensiones a 45°'],
    primary: ['lower_back', 'glutes'],
    secondary: ['hamstrings'],
    equipment: 'bodyweight',
    pattern: 'hinge',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 90,
    cues: [
      'Apoyo justo bajo la cadera para poder flexionarla',
      'Baja con la espalda neutra',
      'Sube apretando glúteos hasta alinear el cuerpo',
      'No pases de la línea recta arriba',
    ],
    mistakes: ['Hiperextender la zona lumbar arriba', 'Hacerlo con impulso'],
  },
  {
    id: 'good-morning',
    name: 'Buenos días con barra',
    aliases: ['Good morning'],
    primary: ['hamstrings', 'lower_back'],
    secondary: ['glutes'],
    equipment: 'barbell',
    pattern: 'hinge',
    compound: true,
    increment: 2.5,
    restSec: 120,
    cues: [
      'Barra en la espalda como en sentadilla',
      'Rodillas algo flexionadas y fijas',
      'Lleva la cadera atrás con la espalda neutra',
      'Baja hasta notar estiramiento en isquios y vuelve',
    ],
    mistakes: ['Redondear la espalda', 'Usar demasiado peso'],
  },
  {
    id: 'barbell-shrug',
    name: 'Encogimientos con barra',
    aliases: ['Encogimientos de hombros', 'Shrugs'],
    primary: ['traps'],
    secondary: ['forearms'],
    equipment: 'barbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'De pie, barra delante de los muslos',
      'Sube los hombros hacia las orejas',
      'Pausa un segundo arriba',
      'Baja del todo controlado',
    ],
    mistakes: ['Rotar los hombros en círculo', 'Doblar los codos'],
  },
  {
    id: 'dumbbell-shrug',
    name: 'Encogimientos con mancuernas',
    aliases: ['Shrugs con mancuernas'],
    primary: ['traps'],
    secondary: ['forearms'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 90,
    cues: [
      'Mancuernas a los lados, brazos rectos',
      'Sube los hombros hacia las orejas',
      'Pausa arriba',
      'Baja del todo',
    ],
    mistakes: ['Rotar los hombros', 'Recorrido corto con mucho peso'],
  },

  /* ───────────── Shoulders ───────────── */
  {
    id: 'overhead-press',
    name: 'Press militar con barra',
    aliases: ['Press militar', 'Press de hombro con barra', 'OHP'],
    primary: ['front_delts'],
    secondary: ['side_delts', 'triceps', 'traps'],
    equipment: 'barbell',
    pattern: 'vertical_push',
    compound: true,
    increment: 2.5,
    restSec: 150,
    cues: [
      'De pie, agarre algo más ancho que los hombros',
      'Aprieta glúteos y abdomen',
      'Aparta la cara y empuja la barra en línea recta',
      'Mete la cabeza bajo la barra al bloquear arriba',
    ],
    mistakes: ['Arquear en exceso la zona lumbar', 'Empujar la barra hacia delante'],
  },
  {
    id: 'seated-dumbbell-press',
    name: 'Press de hombro con mancuernas sentado',
    aliases: ['Press militar con mancuernas'],
    primary: ['front_delts'],
    secondary: ['side_delts', 'triceps'],
    equipment: 'dumbbell',
    pattern: 'vertical_push',
    compound: true,
    increment: 2,
    restSec: 120,
    cues: [
      'Respaldo casi vertical',
      'Mancuernas a la altura de las orejas abajo',
      'Codos algo adelantados, no en cruz',
      'Empuja hasta casi juntarlas arriba',
    ],
    mistakes: ['Despegar la espalda del respaldo', 'Bajar poco'],
  },
  {
    id: 'arnold-press',
    name: 'Press Arnold',
    primary: ['front_delts'],
    secondary: ['side_delts', 'triceps'],
    equipment: 'dumbbell',
    pattern: 'vertical_push',
    compound: true,
    increment: 2,
    restSec: 120,
    cues: [
      'Empieza con las palmas hacia ti a la altura de la barbilla',
      'Gira las muñecas mientras empujas',
      'Termina con las palmas al frente arriba',
      'Deshaz el giro al bajar con control',
    ],
    mistakes: ['Girar después de empujar en vez de a la vez', 'Arquear la espalda'],
  },
  {
    id: 'machine-shoulder-press',
    name: 'Press de hombro en máquina',
    aliases: ['Press militar en máquina'],
    primary: ['front_delts'],
    secondary: ['side_delts', 'triceps'],
    equipment: 'machine',
    pattern: 'vertical_push',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Ajusta el asiento: agarres a la altura de los hombros',
      'Espalda pegada al respaldo',
      'Empuja sin bloquear de golpe',
      'Baja controlado hasta la altura de las orejas',
    ],
    mistakes: ['Asiento demasiado bajo', 'Arquear la zona lumbar'],
  },
  {
    id: 'plate-loaded-shoulder-press',
    name: 'Press de hombro en máquina de discos',
    aliases: ['Press hombro Hammer', 'Press de hombro isolateral'],
    primary: ['front_delts'],
    secondary: ['side_delts', 'triceps'],
    equipment: 'plate_loaded',
    pattern: 'vertical_push',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Agarres a la altura de los hombros al empezar',
      'Espalda y cabeza apoyadas',
      'Empuja siguiendo el arco de la máquina',
      'Controla la bajada',
    ],
    mistakes: ['Despegar la espalda', 'Recorrido corto por exceso de discos'],
  },
  {
    id: 'smith-shoulder-press',
    name: 'Press militar en multipower',
    aliases: ['Press de hombro Smith'],
    primary: ['front_delts'],
    secondary: ['side_delts', 'triceps'],
    equipment: 'smith',
    pattern: 'vertical_push',
    compound: true,
    increment: 2.5,
    restSec: 120,
    cues: [
      'Banco casi vertical bajo la barra',
      'La barra baja por delante de la cara hasta la barbilla',
      'Abdomen tenso, espalda apoyada',
      'Empuja sin bloquear de golpe',
    ],
    mistakes: ['Bajar por detrás de la nuca', 'Banco mal colocado'],
  },
  {
    id: 'dumbbell-lateral-raise',
    name: 'Elevaciones laterales con mancuernas',
    aliases: ['Elevaciones laterales', 'Laterales'],
    primary: ['side_delts'],
    secondary: ['traps'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Torso ligeramente inclinado adelante',
      'Sube los brazos hacia los lados, codos algo flexionados',
      'Lleva los codos hasta la altura de los hombros',
      'Baja despacio sin dejar caer',
    ],
    mistakes: [
      'Balancear el cuerpo',
      'Encoger los hombros hacia las orejas',
      'Subir por encima de los hombros con impulso',
    ],
  },
  {
    id: 'cable-lateral-raise',
    name: 'Elevaciones laterales en polea',
    aliases: ['Laterales en polea', 'Elevación lateral unilateral en polea'],
    primary: ['side_delts'],
    secondary: ['traps'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea baja, cable por delante o por detrás del cuerpo',
      'Sube el brazo hacia el lado con el codo ligeramente flexionado',
      'Para a la altura del hombro',
      'Baja controlado manteniendo tensión',
    ],
    mistakes: ['Inclinarse para usar impulso', 'Encoger el hombro'],
  },
  {
    id: 'machine-lateral-raise',
    name: 'Elevaciones laterales en máquina',
    aliases: ['Laterales en máquina'],
    primary: ['side_delts'],
    secondary: ['traps'],
    equipment: 'machine',
    pattern: 'isolation_upper',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Alinea el hombro con el eje de la máquina',
      'Empuja con los codos, no con las manos',
      'Sube hasta la altura de los hombros',
      'Baja despacio',
    ],
    mistakes: ['Asiento mal ajustado', 'Encoger los hombros'],
  },
  {
    id: 'front-raise',
    name: 'Elevaciones frontales con mancuernas',
    aliases: ['Elevaciones frontales', 'Frontales'],
    primary: ['front_delts'],
    secondary: ['side_delts'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'De pie, mancuernas delante de los muslos',
      'Sube hasta la altura de los hombros',
      'Codos ligeramente flexionados',
      'Baja controlado',
    ],
    mistakes: ['Balancear el torso', 'Subir por encima de la cabeza'],
  },
  {
    id: 'reverse-pec-deck',
    name: 'Pájaros en máquina',
    aliases: ['Contractor inverso', 'Reverse pec deck', 'Aperturas invertidas en máquina'],
    primary: ['rear_delts'],
    secondary: ['traps', 'back'],
    equipment: 'machine',
    pattern: 'isolation_upper',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Pecho apoyado y agarres a la altura de los hombros',
      'Abre los brazos en arco hacia atrás',
      'Piensa en llevar las manos lejos, no en juntar escápulas',
      'Vuelve despacio',
    ],
    mistakes: ['Encoger los hombros', 'Usar impulso del torso'],
  },
  {
    id: 'rear-delt-dumbbell-fly',
    name: 'Pájaros con mancuernas',
    aliases: ['Pájaros', 'Elevaciones posteriores'],
    primary: ['rear_delts'],
    secondary: ['traps', 'back'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Torso inclinado casi paralelo al suelo, espalda neutra',
      'Codos ligeramente flexionados',
      'Abre los brazos hacia los lados',
      'Baja controlado',
    ],
    mistakes: ['Subir el torso para ayudarte', 'Tirar con los codos como un remo'],
  },
  {
    id: 'cable-rear-delt-fly',
    name: 'Pájaros en polea',
    aliases: ['Cruce inverso en polea', 'Aperturas invertidas en polea'],
    primary: ['rear_delts'],
    secondary: ['traps'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Poleas a la altura de los hombros, cables cruzados',
      'Brazos casi rectos',
      'Abre hacia atrás hasta alinear los brazos con el cuerpo',
      'Vuelve despacio',
    ],
    mistakes: ['Doblar los codos', 'Encoger los hombros'],
  },
  {
    id: 'face-pull',
    name: 'Face pull en polea',
    aliases: ['Face pull', 'Tirón a la cara'],
    primary: ['rear_delts'],
    secondary: ['traps', 'side_delts'],
    equipment: 'cable',
    pattern: 'horizontal_pull',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea a la altura de la cara con cuerda',
      'Tira hacia la frente separando las manos',
      'Codos altos y rota los hombros hacia fuera',
      'Pausa un segundo y vuelve controlado',
    ],
    mistakes: ['Echarse atrás para mover el peso', 'Codos por debajo de las manos'],
  },
  {
    id: 'upright-row',
    name: 'Remo al mentón',
    aliases: ['Remo vertical', 'Remo al cuello'],
    primary: ['side_delts', 'traps'],
    secondary: ['front_delts', 'biceps'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea baja con barra, agarre a la anchura de los hombros o más',
      'Sube llevando los codos hacia los lados',
      'Para cuando los codos lleguen a la altura de los hombros',
      'Baja controlado',
    ],
    mistakes: [
      'Agarre muy estrecho que pinza el hombro',
      'Subir los codos por encima de los hombros',
    ],
  },

  /* ───────────── Biceps ───────────── */
  {
    id: 'barbell-curl',
    name: 'Curl con barra recta',
    aliases: ['Curl barra', 'Curl de bíceps con barra'],
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: 'barbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Agarre supino a la anchura de los hombros',
      'Codos pegados a los costados',
      'Sube sin mover el torso',
      'Baja del todo controlado',
    ],
    mistakes: ['Balancear el cuerpo', 'Adelantar los codos al subir'],
  },
  {
    id: 'ez-bar-curl',
    name: 'Curl con barra Z',
    aliases: ['Curl barra Z'],
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: 'ez_bar',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Agarra la barra por las curvas interiores o exteriores',
      'Codos fijos a los costados',
      'Sube hasta contraer y baja del todo',
      'Torso quieto',
    ],
    mistakes: ['Usar impulso de cadera', 'Recorrido corto abajo'],
  },
  {
    id: 'dumbbell-curl',
    name: 'Curl con mancuernas',
    aliases: ['Curl alterno', 'Curl de bíceps con mancuernas'],
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 75,
    cues: [
      'Empieza con las palmas hacia dentro y supina al subir',
      'Codos pegados al cuerpo',
      'Sube hasta contraer el bíceps',
      'Baja del todo sin dejar caer',
    ],
    mistakes: ['Balancear el torso', 'Subir el codo al final'],
  },
  {
    id: 'hammer-curl',
    name: 'Curl martillo',
    aliases: ['Curl martillo con mancuernas', 'Hammer curl'],
    primary: ['biceps', 'forearms'],
    secondary: [],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 75,
    cues: [
      'Agarre neutro, palmas enfrentadas',
      'Codos fijos a los costados',
      'Sube hasta la altura del hombro',
      'Baja controlado',
    ],
    mistakes: ['Balancear el cuerpo', 'Mover los codos adelante'],
  },
  {
    id: 'cable-rope-hammer-curl',
    name: 'Curl martillo en polea con cuerda',
    aliases: ['Curl cuerda polea'],
    primary: ['biceps', 'forearms'],
    secondary: [],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea baja con cuerda, agarre neutro',
      'Codos pegados al cuerpo',
      'Sube hasta contraer',
      'Baja manteniendo tensión',
    ],
    mistakes: ['Echarse atrás', 'Codos hacia delante'],
  },
  {
    id: 'incline-dumbbell-curl',
    name: 'Curl inclinado con mancuernas',
    aliases: ['Curl en banco inclinado'],
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 75,
    cues: [
      'Banco a unos 45–60°, espalda apoyada',
      'Brazos colgando por detrás del torso',
      'Sube sin adelantar los codos',
      'Baja del todo hasta notar estiramiento',
    ],
    mistakes: ['Adelantar los hombros al subir', 'Recortar el estiramiento abajo'],
  },
  {
    id: 'preacher-curl',
    name: 'Curl predicador con barra Z',
    aliases: ['Curl predicador', 'Curl Scott', 'Curl en banco Scott'],
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: 'ez_bar',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Axilas apoyadas en el borde del banco',
      'Baja hasta casi extender el codo',
      'Sube sin despegar los brazos del apoyo',
      'Controla la bajada, sobre todo al final',
    ],
    mistakes: ['Dejar caer el peso abajo', 'Levantar los codos del apoyo'],
  },
  {
    id: 'machine-preacher-curl',
    name: 'Curl predicador en máquina',
    aliases: ['Curl de bíceps en máquina'],
    primary: ['biceps'],
    secondary: [],
    equipment: 'machine',
    pattern: 'isolation_upper',
    compound: false,
    increment: 5,
    restSec: 75,
    cues: [
      'Alinea el codo con el eje de la máquina',
      'Brazos apoyados en el cojín',
      'Sube hasta contraer y baja casi del todo',
      'Ritmo controlado',
    ],
    mistakes: ['Codo desalineado con el eje', 'Levantar los hombros'],
  },
  {
    id: 'concentration-curl',
    name: 'Curl concentrado',
    aliases: ['Curl concentrado con mancuerna'],
    primary: ['biceps'],
    secondary: [],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Sentado, apoya el tríceps en la cara interna del muslo',
      'Sube hacia el hombro sin mover el brazo',
      'Aprieta arriba un segundo',
      'Baja del todo despacio',
    ],
    mistakes: ['Mover el torso para subir', 'Apoyar el codo en lugar del brazo'],
  },
  {
    id: 'cable-curl',
    name: 'Curl en polea baja',
    aliases: ['Curl en polea', 'Curl con barra en polea'],
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'De pie frente a la polea baja',
      'Codos fijos a los costados',
      'Sube hasta contraer',
      'Baja manteniendo tensión',
    ],
    mistakes: ['Echarse atrás', 'Adelantar los codos'],
  },
  {
    id: 'bayesian-curl',
    name: 'Curl bayesiano en polea',
    aliases: ['Curl de espaldas a la polea', 'Bayesian curl'],
    primary: ['biceps'],
    secondary: [],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'De espaldas a la polea baja, un paso adelante',
      'Brazo por detrás del cuerpo al empezar',
      'Sube sin adelantar el codo',
      'Vuelve despacio hasta el estiramiento',
    ],
    mistakes: ['Llevar el codo adelante', 'Girar el torso'],
    trend: {
      by: 'Menno Henselmans y Jeff Nippard',
      why: 'Con el brazo por detrás del cuerpo el bíceps se carga en su posición más estirada, la que más hipertrofia parece generar.',
    },
  },
  {
    id: 'spider-curl',
    name: 'Curl araña',
    aliases: ['Spider curl'],
    primary: ['biceps'],
    secondary: [],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Pecho apoyado en un banco inclinado',
      'Brazos colgando verticales',
      'Sube hasta contraer con los codos quietos',
      'Baja del todo',
    ],
    mistakes: ['Balancear los brazos', 'Recorrido corto'],
  },
  {
    id: 'reverse-curl',
    name: 'Curl invertido con barra Z',
    aliases: ['Curl prono', 'Curl inverso'],
    primary: ['forearms'],
    secondary: ['biceps'],
    equipment: 'ez_bar',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Agarre prono a la anchura de los hombros',
      'Muñecas rectas',
      'Codos fijos a los costados',
      'Baja controlado',
    ],
    mistakes: ['Doblar las muñecas', 'Usar impulso'],
  },

  /* ───────────── Triceps ───────────── */
  {
    id: 'skull-crusher',
    name: 'Press francés con barra Z',
    aliases: ['Press francés', 'Rompecráneos', 'Skull crusher'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'ez_bar',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Tumbado, barra sobre los hombros con brazos algo inclinados atrás',
      'Baja la barra hacia la frente o detrás de la cabeza',
      'Codos apuntando al techo, sin abrirlos',
      'Extiende los codos sin mover los hombros',
    ],
    mistakes: ['Abrir los codos hacia fuera', 'Mover los hombros y convertirlo en press'],
  },
  {
    id: 'triceps-pushdown',
    name: 'Extensión de tríceps en polea alta',
    aliases: ['Extensión polea alta', 'Jalón de tríceps', 'Pushdown', 'Tríceps en polea'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Codos pegados a los costados y fijos',
      'Torso ligeramente inclinado',
      'Extiende del todo y aprieta abajo',
      'Sube hasta que el antebrazo pase de la horizontal',
    ],
    mistakes: ['Mover los codos adelante y atrás', 'Echar el cuerpo encima del peso'],
  },
  {
    id: 'rope-pushdown',
    name: 'Extensión de tríceps en polea con cuerda',
    aliases: ['Tríceps con cuerda', 'Pushdown con cuerda'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Codos fijos a los costados',
      'Extiende y separa los extremos de la cuerda abajo',
      'Pausa un segundo con el tríceps contraído',
      'Sube controlado',
    ],
    mistakes: ['Mover los codos', 'Usar los hombros para empujar'],
  },
  {
    id: 'overhead-cable-extension',
    name: 'Extensión de tríceps sobre la cabeza en polea',
    aliases: ['Extensión overhead en polea', 'Extensión tras nuca en polea', 'Overhead extension'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'De espaldas a la polea, cuerda por detrás de la cabeza',
      'Codos apuntando adelante y quietos',
      'Extiende del todo los brazos',
      'Vuelve hasta notar estiramiento en el tríceps',
    ],
    mistakes: ['Abrir los codos', 'Arquear la zona lumbar'],
  },
  {
    id: 'overhead-dumbbell-extension',
    name: 'Extensión de tríceps tras nuca con mancuerna',
    aliases: ['Press francés con mancuerna', 'Extensión tras nuca'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Sentado con respaldo, mancuerna sujeta con ambas manos',
      'Codos apuntando al techo',
      'Baja por detrás de la cabeza hasta el estiramiento',
      'Extiende sin mover los codos',
    ],
    mistakes: ['Abrir los codos', 'Arquear la espalda'],
  },
  {
    id: 'dumbbell-kickback',
    name: 'Patada de tríceps con mancuerna',
    aliases: ['Patada de tríceps', 'Kickback'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Torso inclinado y brazo pegado al cuerpo, paralelo al suelo',
      'Extiende el codo hasta bloquear',
      'Aprieta el tríceps un segundo',
      'Vuelve sin bajar el codo',
    ],
    mistakes: ['Dejar caer el codo', 'Balancear la mancuerna'],
  },
  {
    id: 'machine-triceps-extension',
    name: 'Extensión de tríceps en máquina',
    aliases: ['Tríceps en máquina'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'machine',
    pattern: 'isolation_upper',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Alinea el codo con el eje de la máquina',
      'Brazos apoyados en el cojín',
      'Extiende del todo y aprieta',
      'Vuelve controlado',
    ],
    mistakes: ['Asiento mal ajustado', 'Levantar los hombros'],
  },
  {
    id: 'bench-dip',
    name: 'Fondos en banco',
    aliases: ['Fondos de tríceps en banco'],
    primary: ['triceps'],
    secondary: ['chest', 'front_delts'],
    equipment: 'bodyweight',
    pattern: 'vertical_push',
    compound: true,
    increment: 2.5,
    bodyweight: true,
    restSec: 60,
    cues: [
      'Manos en el borde del banco, dedos hacia delante',
      'Cadera cerca del banco',
      'Baja hasta que los codos formen unos 90°',
      'Empuja hasta extender los codos',
    ],
    mistakes: ['Bajar demasiado y forzar el hombro', 'Separar la cadera del banco'],
  },

  /* ───────────── Forearms & carries ───────────── */
  {
    id: 'wrist-curl',
    name: 'Curl de muñeca con barra',
    aliases: ['Curl de muñeca', 'Flexión de muñeca'],
    primary: ['forearms'],
    secondary: [],
    equipment: 'barbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Antebrazos apoyados en el banco, palmas arriba',
      'Deja rodar la barra hacia los dedos',
      'Flexiona las muñecas al máximo',
      'Ritmo controlado',
    ],
    mistakes: ['Levantar los antebrazos del apoyo', 'Recorrido corto'],
  },
  {
    id: 'reverse-wrist-curl',
    name: 'Extensión de muñeca con barra',
    aliases: ['Curl de muñeca invertido'],
    primary: ['forearms'],
    secondary: [],
    equipment: 'barbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Antebrazos apoyados, palmas abajo',
      'Extiende las muñecas hacia arriba',
      'Baja despacio',
    ],
    mistakes: ['Usar demasiado peso', 'Mover los antebrazos'],
  },
  {
    id: 'farmer-walk',
    name: 'Paseo del granjero',
    aliases: ['Farmer walk', 'Caminata del granjero'],
    primary: ['forearms', 'traps'],
    secondary: ['abs', 'glutes'],
    equipment: 'dumbbell',
    pattern: 'carry',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Coge las mancuernas con la espalda neutra',
      'Hombros atrás y abajo, pecho alto',
      'Pasos cortos y firmes',
      'Abdomen tenso, sin inclinarte',
    ],
    mistakes: ['Encorvarse', 'Dejar que las mancuernas golpeen las piernas'],
  },

  /* ───────────── Quads & legs ───────────── */
  {
    id: 'squat',
    name: 'Sentadilla con barra',
    aliases: ['Sentadilla', 'Sentadilla trasera', 'Back squat'],
    primary: ['quads', 'glutes'],
    secondary: ['adductors', 'lower_back', 'hamstrings'],
    equipment: 'barbell',
    pattern: 'squat',
    compound: true,
    increment: 2.5,
    restSec: 180,
    cues: [
      'Barra sobre los trapecios, escápulas juntas',
      'Pies a la anchura de los hombros, puntas algo hacia fuera',
      'Coge aire y aprieta el abdomen antes de bajar',
      'Rodillas en la dirección de los pies',
      'Baja al menos hasta la paralela y sube empujando todo el pie',
    ],
    mistakes: [
      'Rodillas hacia dentro al subir',
      'Levantar los talones',
      'Redondear la zona lumbar abajo',
    ],
  },
  {
    id: 'front-squat',
    name: 'Sentadilla frontal',
    aliases: ['Front squat'],
    primary: ['quads'],
    secondary: ['glutes', 'abs', 'traps'],
    equipment: 'barbell',
    pattern: 'squat',
    compound: true,
    increment: 2.5,
    restSec: 180,
    cues: [
      'Barra sobre los deltoides, codos altos',
      'Torso vertical durante todo el recorrido',
      'Abdomen tenso antes de bajar',
      'Baja profundo con las rodillas adelante',
    ],
    mistakes: ['Dejar caer los codos', 'Inclinar el torso adelante'],
  },
  {
    id: 'smith-squat',
    name: 'Sentadilla en multipower',
    aliases: ['Sentadilla Smith'],
    primary: ['quads', 'glutes'],
    secondary: ['adductors'],
    equipment: 'smith',
    pattern: 'squat',
    compound: true,
    increment: 2.5,
    restSec: 150,
    cues: [
      'Pies ligeramente adelantados respecto a la barra',
      'Espalda neutra y abdomen tenso',
      'Baja hasta la paralela o más',
      'Empuja con todo el pie',
    ],
    mistakes: ['Pies demasiado atrás que cargan las rodillas', 'Recorrido corto'],
  },
  {
    id: 'hack-squat',
    name: 'Sentadilla hack',
    aliases: ['Hack squat', 'Máquina hack'],
    primary: ['quads'],
    secondary: ['glutes', 'adductors'],
    equipment: 'plate_loaded',
    pattern: 'squat',
    compound: true,
    increment: 5,
    restSec: 150,
    cues: [
      'Espalda y hombros pegados al respaldo',
      'Pies a la anchura de la cadera en el centro de la plataforma',
      'Baja profundo con control',
      'Empuja sin bloquear las rodillas de golpe',
    ],
    mistakes: ['Despegar los talones', 'Recorrido corto por exceso de peso'],
  },
  {
    id: 'leg-press',
    name: 'Prensa 45°',
    aliases: ['Prensa inclinada', 'Prensa de piernas', 'Prensa inclinada 45°'],
    primary: ['quads', 'glutes'],
    secondary: ['adductors', 'hamstrings'],
    equipment: 'plate_loaded',
    pattern: 'squat',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Espalda y glúteos pegados al respaldo',
      'Pies a la anchura de la cadera en mitad de la plataforma',
      'Baja hasta que las rodillas lleguen cerca del pecho sin despegar la pelvis',
      'Empuja sin bloquear las rodillas',
    ],
    mistakes: [
      'Despegar la pelvis abajo',
      'Bloquear las rodillas de golpe',
      'Recorrido corto con mucho peso',
    ],
  },
  {
    id: 'horizontal-leg-press',
    name: 'Prensa horizontal',
    aliases: ['Prensa sentado', 'Prensa de placas'],
    primary: ['quads', 'glutes'],
    secondary: ['adductors', 'hamstrings'],
    equipment: 'machine',
    pattern: 'squat',
    compound: true,
    increment: 5,
    restSec: 120,
    cues: [
      'Ajusta el respaldo para empezar con las rodillas bien flexionadas',
      'Espalda pegada al respaldo',
      'Empuja con todo el pie',
      'Vuelve controlado',
    ],
    mistakes: ['Bloquear las rodillas', 'Despegar la pelvis'],
  },
  {
    id: 'goblet-squat',
    name: 'Sentadilla goblet',
    aliases: ['Sentadilla copa'],
    primary: ['quads', 'glutes'],
    secondary: ['adductors', 'abs'],
    equipment: 'dumbbell',
    pattern: 'squat',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Sujeta la mancuerna pegada al pecho',
      'Torso erguido',
      'Baja entre las piernas con las rodillas hacia fuera',
      'Sube empujando con todo el pie',
    ],
    mistakes: ['Inclinar el torso', 'Rodillas hacia dentro'],
  },
  {
    id: 'bulgarian-split-squat',
    name: 'Sentadilla búlgara',
    aliases: ['Búlgara', 'Zancada búlgara'],
    primary: ['quads', 'glutes'],
    secondary: ['adductors', 'hamstrings'],
    equipment: 'dumbbell',
    pattern: 'lunge',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Pie trasero apoyado en el banco',
      'Pie delantero lo bastante adelantado para bajar recto',
      'Baja hasta que la rodilla trasera casi toque el suelo',
      'Más inclinación de torso = más glúteo',
    ],
    mistakes: ['Pie delantero demasiado cerca del banco', 'Rodilla delantera hacia dentro'],
  },
  {
    id: 'walking-lunge',
    name: 'Zancadas con mancuernas',
    aliases: ['Zancadas', 'Lunges', 'Zancadas caminando'],
    primary: ['quads', 'glutes'],
    secondary: ['adductors', 'hamstrings'],
    equipment: 'dumbbell',
    pattern: 'lunge',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Paso largo y estable',
      'Baja hasta que la rodilla trasera casi toque el suelo',
      'Torso erguido',
      'Empuja con el talón delantero',
    ],
    mistakes: ['Pasos cortos que cargan la rodilla', 'Perder el equilibrio por ir rápido'],
  },
  {
    id: 'step-up',
    name: 'Subidas al banco',
    aliases: ['Step-up', 'Subidas al cajón'],
    primary: ['quads', 'glutes'],
    secondary: ['hamstrings'],
    equipment: 'dumbbell',
    pattern: 'lunge',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Apoya todo el pie en el banco',
      'Sube con la pierna de arriba, no impulsándote con la de abajo',
      'Baja controlado',
    ],
    mistakes: ['Impulsarse con la pierna de abajo', 'Banco demasiado alto'],
  },
  {
    id: 'leg-extension',
    name: 'Extensión de cuádriceps',
    aliases: ['Extensiones de pierna', 'Cuádriceps en máquina', 'Sillón de cuádriceps'],
    primary: ['quads'],
    secondary: [],
    equipment: 'machine',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 75,
    cues: [
      'Alinea la rodilla con el eje de la máquina',
      'Rodillo sobre el tobillo',
      'Extiende del todo y aprieta un segundo',
      'Baja controlado',
    ],
    mistakes: ['Levantar la cadera del asiento', 'Soltar el peso abajo'],
  },
  {
    id: 'lying-leg-curl',
    name: 'Curl femoral tumbado',
    aliases: ['Femoral tumbado', 'Curl de isquios tumbado'],
    primary: ['hamstrings'],
    secondary: ['calves'],
    equipment: 'machine',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 75,
    cues: [
      'Rodilla alineada con el eje, rodillo sobre el tendón de Aquiles',
      'Cadera pegada al banco',
      'Flexiona hasta llevar el rodillo cerca de los glúteos',
      'Baja despacio',
    ],
    mistakes: ['Levantar la cadera', 'Recorrido corto'],
  },
  {
    id: 'seated-leg-curl',
    name: 'Curl femoral sentado',
    aliases: ['Femoral sentado'],
    primary: ['hamstrings'],
    secondary: ['calves'],
    equipment: 'machine',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 75,
    cues: [
      'Rodilla alineada con el eje',
      'Ajusta el rodillo de muslos firme',
      'Inclina el torso adelante para estirar más los isquios',
      'Flexiona del todo y vuelve despacio',
    ],
    mistakes: ['Rodillo de muslos suelto', 'Soltar el peso al volver'],
    trend: {
      by: 'Menno Henselmans y Joe Bennett (Hypertrophy Coach)',
      why: 'Inclinarte hacia delante flexiona la cadera y alarga los isquios, así trabajan más estirados que tumbado o con la espalda apoyada.',
    },
  },
  {
    id: 'romanian-deadlift',
    name: 'Peso muerto rumano',
    aliases: ['Rumano', 'RDL'],
    primary: ['hamstrings', 'glutes'],
    secondary: ['lower_back', 'forearms'],
    equipment: 'barbell',
    pattern: 'hinge',
    compound: true,
    increment: 2.5,
    restSec: 120,
    cues: [
      'Rodillas algo flexionadas y fijas',
      'Lleva la cadera atrás con la espalda neutra',
      'Barra pegada a las piernas',
      'Baja hasta notar estiramiento fuerte en isquios',
      'Sube empujando la cadera adelante',
    ],
    mistakes: ['Redondear la espalda', 'Doblar las rodillas como en sentadilla'],
  },
  {
    id: 'dumbbell-romanian-deadlift',
    name: 'Peso muerto rumano con mancuernas',
    aliases: ['Rumano con mancuernas'],
    primary: ['hamstrings', 'glutes'],
    secondary: ['lower_back', 'forearms'],
    equipment: 'dumbbell',
    pattern: 'hinge',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Mancuernas pegadas a los muslos',
      'Cadera atrás y espalda neutra',
      'Baja hasta notar estiramiento en isquios',
      'Sube apretando glúteos',
    ],
    mistakes: ['Redondear la espalda', 'Alejar las mancuernas del cuerpo'],
  },
  {
    id: 'hip-thrust',
    name: 'Hip thrust con barra',
    aliases: ['Hip thrust', 'Empuje de cadera'],
    primary: ['glutes'],
    secondary: ['hamstrings', 'quads'],
    equipment: 'barbell',
    pattern: 'hinge',
    compound: true,
    increment: 2.5,
    restSec: 120,
    cues: [
      'Escápulas apoyadas en el banco, barra sobre la cadera con protector',
      'Pies separados a la anchura de la cadera',
      'Barbilla metida y costillas abajo',
      'Sube hasta alinear tronco y muslos y aprieta glúteos',
    ],
    mistakes: ['Hiperextender la zona lumbar arriba', 'Pies demasiado lejos o cerca'],
  },
  {
    id: 'machine-hip-thrust',
    name: 'Hip thrust en máquina',
    aliases: ['Glute drive', 'Máquina de glúteo'],
    primary: ['glutes'],
    secondary: ['hamstrings'],
    equipment: 'machine',
    pattern: 'hinge',
    compound: true,
    increment: 5,
    restSec: 90,
    cues: [
      'Cinturón o almohadilla sobre la cadera',
      'Barbilla metida y costillas abajo',
      'Extiende la cadera hasta la línea recta',
      'Pausa arriba un segundo',
    ],
    mistakes: ['Arquear la zona lumbar', 'Recorrido corto'],
  },
  {
    id: 'cable-glute-kickback',
    name: 'Patada de glúteo en polea',
    aliases: ['Patada de glúteo', 'Kickback en polea'],
    primary: ['glutes'],
    secondary: ['hamstrings'],
    equipment: 'cable',
    pattern: 'isolation_lower',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Tobillera en la polea baja',
      'Torso algo inclinado y cadera estable',
      'Lleva la pierna atrás apretando el glúteo',
      'Vuelve controlado',
    ],
    mistakes: ['Arquear la zona lumbar', 'Rotar la cadera'],
  },
  {
    id: 'machine-glute-kickback',
    name: 'Patada de glúteo en máquina',
    aliases: ['Glúteo en máquina'],
    primary: ['glutes'],
    secondary: ['hamstrings'],
    equipment: 'machine',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Torso apoyado y abdomen tenso',
      'Empuja con el talón hacia atrás',
      'Para al alinear la pierna con el cuerpo',
      'Vuelve controlado',
    ],
    mistakes: ['Arquear la zona lumbar', 'Usar impulso'],
  },
  {
    id: 'hip-abduction',
    name: 'Abductores en máquina',
    aliases: ['Abducción de cadera', 'Máquina de abductores'],
    primary: ['glutes'],
    secondary: [],
    equipment: 'machine',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Espalda apoyada o torso algo inclinado adelante',
      'Abre las piernas hasta el máximo recorrido',
      'Pausa un segundo',
      'Cierra despacio',
    ],
    mistakes: ['Soltar el peso al cerrar', 'Balancear el torso'],
  },
  {
    id: 'hip-adduction',
    name: 'Aductores en máquina',
    aliases: ['Aducción de cadera', 'Máquina de aductores'],
    primary: ['adductors'],
    secondary: [],
    equipment: 'machine',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Empieza con una apertura que puedas controlar',
      'Cierra las piernas apretando',
      'Abre despacio hasta notar estiramiento',
    ],
    mistakes: ['Abrir más de lo que controlas', 'Usar impulso'],
  },
  {
    id: 'cable-pull-through',
    name: 'Pull through en polea',
    aliases: ['Pull through'],
    primary: ['glutes', 'hamstrings'],
    secondary: ['lower_back'],
    equipment: 'cable',
    pattern: 'hinge',
    compound: true,
    increment: 2.5,
    restSec: 90,
    cues: [
      'De espaldas a la polea baja con la cuerda entre las piernas',
      'Lleva la cadera atrás con la espalda neutra',
      'Extiende la cadera apretando glúteos',
      'No tires con los brazos',
    ],
    mistakes: ['Flexionar demasiado las rodillas', 'Hiperextender la zona lumbar arriba'],
  },
  {
    id: 'kettlebell-swing',
    name: 'Swing con kettlebell',
    aliases: ['Swing ruso', 'Kettlebell swing'],
    primary: ['glutes', 'hamstrings'],
    secondary: ['lower_back', 'abs', 'forearms'],
    equipment: 'kettlebell',
    pattern: 'hinge',
    compound: true,
    increment: 4,
    restSec: 90,
    cues: [
      'Es una bisagra de cadera, no una sentadilla',
      'Lleva la kettlebell atrás entre las piernas',
      'Extiende la cadera explosivo; los brazos solo guían',
      'Para a la altura del pecho con glúteos y abdomen apretados',
    ],
    mistakes: ['Subir la kettlebell con los hombros', 'Redondear la espalda'],
  },
  {
    id: 'standing-calf-raise',
    name: 'Gemelos de pie',
    aliases: ['Elevación de talones de pie', 'Gemelos en máquina'],
    primary: ['calves'],
    secondary: [],
    equipment: 'machine',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Puntas en el borde, talones libres',
      'Rodillas extendidas',
      'Baja hasta el máximo estiramiento y pausa',
      'Sube hasta la punta y aprieta',
    ],
    mistakes: ['Rebotar abajo', 'Recorrido corto'],
  },
  {
    id: 'seated-calf-raise',
    name: 'Gemelos sentado',
    aliases: ['Elevación de talones sentado', 'Sóleo'],
    primary: ['calves'],
    secondary: [],
    equipment: 'plate_loaded',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Almohadilla sobre la parte baja de los muslos',
      'Puntas en el borde',
      'Baja hasta el máximo estiramiento y pausa',
      'Sube del todo',
    ],
    mistakes: ['Rebotar', 'Medias repeticiones'],
  },
  {
    id: 'leg-press-calf-raise',
    name: 'Gemelos en prensa',
    aliases: ['Gemelos prensa'],
    primary: ['calves'],
    secondary: [],
    equipment: 'plate_loaded',
    pattern: 'isolation_lower',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Puntas en el borde inferior de la plataforma',
      'Rodillas extendidas pero no bloqueadas',
      'Estira del todo y pausa abajo',
      'Empuja hasta la punta',
    ],
    mistakes: ['Pies resbalando de la plataforma', 'Recorrido corto'],
  },

  /* ───────────── Core ───────────── */
  {
    id: 'plank',
    name: 'Plancha',
    aliases: ['Plancha abdominal', 'Plank'],
    primary: ['abs'],
    secondary: ['lower_back'],
    equipment: 'bodyweight',
    pattern: 'core',
    timed: true,
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 60,
    cues: [
      'Antebrazos bajo los hombros',
      'Cuerpo en línea recta',
      'Aprieta glúteos y abdomen, pelvis en retroversión',
      'Respira sin perder la posición',
    ],
    mistakes: ['Dejar caer la cadera', 'Subir mucho los glúteos'],
  },
  {
    id: 'side-plank',
    name: 'Plancha lateral',
    aliases: ['Side plank'],
    primary: ['abs'],
    secondary: ['glutes'],
    equipment: 'bodyweight',
    pattern: 'core',
    timed: true,
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 45,
    cues: [
      'Codo bajo el hombro',
      'Cuerpo en línea recta de cabeza a pies',
      'Empuja la cadera hacia arriba',
    ],
    mistakes: ['Dejar caer la cadera', 'Rotar el torso'],
  },
  {
    id: 'ab-wheel',
    name: 'Rueda abdominal',
    aliases: ['Ab wheel', 'Rueda de abdominales'],
    primary: ['abs'],
    secondary: ['back', 'lower_back'],
    equipment: 'bodyweight',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 60,
    cues: [
      'De rodillas, pelvis en retroversión',
      'Rueda adelante sin arquear la zona lumbar',
      'Llega solo hasta donde mantienes la espalda neutra',
      'Vuelve tirando con el abdomen',
    ],
    mistakes: ['Arquear la zona lumbar', 'Ir más lejos de lo que controlas'],
  },
  {
    id: 'cable-crunch',
    name: 'Crunch en polea alta',
    aliases: ['Encogimientos en polea', 'Crunch de rodillas en polea'],
    primary: ['abs'],
    secondary: [],
    equipment: 'cable',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'De rodillas, cuerda junto a la cabeza',
      'Flexiona la columna llevando costillas a pelvis',
      'La cadera no se mueve',
      'Vuelve despacio estirando el abdomen',
    ],
    mistakes: ['Tirar con los brazos', 'Sentarse sobre los talones en vez de flexionar'],
  },
  {
    id: 'hanging-leg-raise',
    name: 'Elevaciones de piernas colgado',
    aliases: ['Elevación de piernas en barra', 'Hanging leg raise'],
    primary: ['abs'],
    secondary: ['forearms'],
    equipment: 'bodyweight',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 60,
    cues: [
      'Cuélgate con los hombros activos',
      'Sube las piernas enrollando la pelvis',
      'Evita el balanceo',
      'Baja despacio',
    ],
    mistakes: ['Balancearse', 'Subir solo las piernas sin flexionar la pelvis'],
  },
  {
    id: 'captains-chair-knee-raise',
    name: 'Elevaciones de rodillas en paralelas',
    aliases: ['Silla romana', "Captain's chair", 'Elevación de rodillas'],
    primary: ['abs'],
    secondary: [],
    equipment: 'bodyweight',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 60,
    cues: [
      'Espalda apoyada en el respaldo',
      'Sube las rodillas al pecho despegando la pelvis',
      'Baja controlado sin balanceo',
    ],
    mistakes: ['Solo flexionar la cadera', 'Balancear las piernas'],
  },
  {
    id: 'crunch',
    name: 'Crunch abdominal',
    aliases: ['Encogimientos abdominales', 'Abdominales'],
    primary: ['abs'],
    secondary: [],
    equipment: 'bodyweight',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 45,
    cues: [
      'Tumbado, rodillas flexionadas',
      'Enrolla la columna despegando las escápulas',
      'No tires del cuello',
      'Baja despacio',
    ],
    mistakes: ['Tirar de la cabeza con las manos', 'Subir con impulso'],
  },
  {
    id: 'machine-crunch',
    name: 'Crunch en máquina',
    aliases: ['Abdominales en máquina'],
    primary: ['abs'],
    secondary: [],
    equipment: 'machine',
    pattern: 'core',
    compound: false,
    increment: 5,
    restSec: 60,
    cues: [
      'Ajusta el asiento para que el eje quede a la altura del abdomen',
      'Flexiona la columna, no la cadera',
      'Vuelve despacio',
    ],
    mistakes: ['Empujar con los brazos', 'Usar impulso'],
  },
  {
    id: 'pallof-press',
    name: 'Press Pallof',
    aliases: ['Pallof press', 'Antirrotación en polea'],
    primary: ['abs'],
    secondary: [],
    equipment: 'cable',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    restSec: 45,
    cues: [
      'De lado a la polea a la altura del pecho',
      'Empuja las manos al frente sin dejar que el torso gire',
      'Aguanta un segundo extendido',
      'Vuelve al pecho controlado',
    ],
    mistakes: ['Girar el torso hacia la polea', 'Estar demasiado cerca de la polea'],
  },
  {
    id: 'dead-bug',
    name: 'Dead bug',
    aliases: ['Bicho muerto'],
    primary: ['abs'],
    secondary: [],
    equipment: 'bodyweight',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 45,
    cues: [
      'Tumbado, zona lumbar pegada al suelo',
      'Extiende brazo y pierna contrarios despacio',
      'Vuelve y cambia de lado',
    ],
    mistakes: ['Despegar la zona lumbar', 'Ir rápido'],
  },
  {
    id: 'cable-woodchop',
    name: 'Leñador en polea',
    aliases: ['Woodchop', 'Leñador'],
    primary: ['abs'],
    secondary: [],
    equipment: 'cable',
    pattern: 'core',
    compound: false,
    increment: 2.5,
    restSec: 45,
    cues: [
      'De lado a la polea, brazos casi rectos',
      'Gira desde el tronco y la cadera, no con los brazos',
      'Controla la vuelta',
    ],
    mistakes: ['Tirar solo con los brazos', 'Girar con la zona lumbar sin mover la cadera'],
  },

  /* ───────────── Novedades: variantes popularizadas por divulgadores (2023–2026) ───────────── */
  {
    id: 'cable-lat-pull-around',
    name: 'Jalón cruzado unilateral en polea (lat pull-around)',
    aliases: [
      'Lat pull-around',
      'Pull-around',
      'Jalón cruzado',
      'Jalón unilateral cruzado',
      'Cross-body lat pull-around',
      'Pull de polea cruzado',
    ],
    primary: ['back'],
    secondary: ['biceps', 'rear_delts'],
    equipment: 'cable',
    pattern: 'vertical_pull',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Polea a media altura o algo más alta, agarre con la mano del lado contrario',
      'Rodilla del lado de la polea al suelo y torso girado de lado a la torre',
      'Empieza con el brazo cruzado por delante y el dorsal bien estirado',
      'Barre el codo hacia abajo y atrás hasta pegar la mano a la cadera',
      'Vuelve despacio hasta el estiramiento completo',
    ],
    mistakes: [
      'Rotar el tronco para mover más peso',
      'Tirar con el bíceps en vez de llevar el codo a la cadera',
      'Cortar la vuelta y perder el estiramiento',
    ],
    trend: {
      by: 'Jeff Nippard y Jesse James West',
      why: 'Al tirar en diagonal desde el lado contrario la línea de tracción sigue las fibras del dorsal y lo carga muy estirado, algo que el jalón normal no consigue.',
    },
  },
  {
    id: 'half-kneeling-single-arm-pulldown',
    name: 'Jalón unilateral de rodillas en polea alta',
    aliases: [
      'Jalón a una mano de rodillas',
      'Half-kneeling 1-arm lat pulldown',
      'Jalón unilateral',
    ],
    primary: ['back'],
    secondary: ['biceps', 'rear_delts'],
    equipment: 'cable',
    pattern: 'vertical_pull',
    compound: false,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Rodilla del lado que trabaja al suelo, polea alta delante',
      'Deja que el brazo suba del todo y el hombro se eleve para estirar',
      'Tira llevando el codo hacia el bolsillo trasero',
      'Torso quieto, ligeramente inclinado hacia atrás',
    ],
    mistakes: ['Tirar con la mano por delante del cuerpo', 'Inclinarse de lado para ayudarse'],
    trend: {
      by: 'Jeff Nippard',
      why: 'A una mano el recorrido es más largo y el codo puede bajar pegado al costado, alineado con las fibras del dorsal.',
    },
  },
  {
    id: 'lat-prayer',
    name: 'Lat prayer en polea',
    aliases: ['Pullover de rodillas con cuerda', 'Rezo de dorsal', 'Lat prayer'],
    primary: ['back'],
    secondary: ['triceps', 'abs'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 75,
    cues: [
      'De rodillas frente a la polea alta, cuerda o barra recta',
      'Cadera atrás y torso inclinado hasta notar los dorsales estirados',
      'Codos algo flexionados y fijos',
      'Lleva las manos hacia la cabeza y la cadera hacia delante a la vez',
    ],
    mistakes: ['Doblar los codos y convertirlo en un press', 'Arquear la zona lumbar al final'],
    trend: {
      by: 'Menno Henselmans y Renaissance Periodization',
      why: 'Aísla el dorsal sin que el bíceps limite, y la posición inclinada lo carga en su máximo estiramiento.',
    },
  },
  {
    id: 'cable-y-raise',
    name: 'Elevación en Y en polea cruzada',
    aliases: ['Cable Y-raise', 'Y-raise', 'Elevaciones laterales cruzadas en polea'],
    primary: ['side_delts'],
    secondary: ['traps', 'rear_delts'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Poleas bajas, cables cruzados: mano derecha al cable izquierdo y al revés',
      'Un paso atrás y ligera inclinación del torso',
      'Sube en diagonal formando una Y por encima de los hombros',
      'Hombros abajo, sin encoger',
    ],
    mistakes: ['Encoger los hombros al subir', 'Usar el cuerpo para lanzar el peso'],
    trend: {
      by: 'Jeff Nippard y Dr. Mike Israetel',
      why: 'La polea mantiene la tensión abajo, donde la mancuerna no carga, y la trayectoria en Y sigue el plano natural del hombro.',
    },
  },
  {
    id: 'behind-back-cable-lateral-raise',
    name: 'Elevación lateral en polea por detrás',
    aliases: ['Elevación lateral por detrás del cuerpo', 'Behind-the-back cable lateral raise'],
    primary: ['side_delts'],
    secondary: ['traps'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea a la altura de la muñeca o algo más alta, cable por detrás de ti',
      'Empieza con la mano detrás de la cadera del lado contrario',
      'Sube en abanico hasta la altura del hombro',
      'Baja despacio hasta notar el estiramiento',
    ],
    mistakes: ['Polea en el suelo: pierdes la tensión abajo', 'Inclinarse para usar impulso'],
    trend: {
      by: 'Jeremy Ethier y Jeff Nippard',
      why: 'Con el brazo detrás del cuerpo el deltoides lateral empieza estirado y la polea lo carga justo ahí, donde más crece.',
    },
  },
  {
    id: 'super-rom-lateral-raise',
    name: 'Elevación lateral Super ROM (Lu raise)',
    aliases: [
      'Elevaciones Lu',
      'Lu raise',
      'Super ROM lateral raise',
      'Elevación lateral completa',
    ],
    primary: ['side_delts'],
    secondary: ['traps', 'front_delts'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Mancuernas ligeras, pulgares hacia arriba',
      'Sube en lateral y sigue por encima de la cabeza',
      'Deja que las escápulas roten arriba al final',
      'Baja controlado todo el recorrido',
    ],
    mistakes: [
      'Peso excesivo que obliga a balancear',
      'Arquear la espalda al pasar de los hombros',
    ],
    trend: {
      by: 'Lu Xiaojun; recuperada por Dr. Mike Israetel y Jeff Nippard',
      why: 'Recorrido completo: el deltoides trabaja todo el arco y el trapecio superior entra al final, con poco peso y sin molestias.',
    },
  },
  {
    id: 'side-lying-incline-lateral-raise',
    name: 'Elevación lateral tumbado de lado en banco inclinado',
    aliases: ['Elevación lateral tumbado', 'Lying side raise', 'Elevación lateral estilo Arnold'],
    primary: ['side_delts'],
    secondary: [],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Túmbate de lado sobre un banco inclinado a 30–45°',
      'Brazo de trabajo cruzado delante del cuerpo al empezar',
      'Sube hasta que el brazo quede casi vertical',
      'Baja despacio hasta el estiramiento',
    ],
    mistakes: ['Usar demasiado peso y doblar el codo', 'Rodar el cuerpo para ayudar'],
    trend: {
      by: 'Jeff Nippard',
      why: 'Tumbado de lado la resistencia es máxima al inicio, con el deltoides estirado, justo al revés que la elevación de pie.',
    },
  },
  {
    id: 'lean-in-lateral-raise',
    name: 'Elevación lateral inclinado hacia dentro',
    aliases: ['Lean-in lateral raise', 'Elevación lateral apoyado inclinado'],
    primary: ['side_delts'],
    secondary: ['traps'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Apoya el antebrazo libre en un banco inclinado o un soporte',
      'Inclina el torso hacia ese lado unos 15–30°',
      'Mancuerna por delante del muslo, sube en lateral',
      'Para a la altura del hombro y baja despacio',
    ],
    mistakes: ['Inclinarte tan poco que no cambia nada', 'Balancear el torso al subir'],
    trend: {
      by: 'Jeremy Ethier y Jeff Nippard',
      why: 'Al inclinarte hacia dentro la mancuerna pesa más al principio, con el deltoides estirado, en vez de solo arriba.',
    },
  },
  {
    id: 'katana-triceps-extension',
    name: 'Extensión de tríceps katana en polea',
    aliases: [
      'Katana',
      'Extensión de tríceps cruzada en polea',
      'Cross-body cable triceps extension',
      'Katana extension',
    ],
    primary: ['triceps'],
    secondary: [],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea a la altura de la cintura, agarre con la mano del lado contrario',
      'Codo alto, junto a la cabeza, mano detrás del hombro opuesto',
      'Extiende el brazo en diagonal, como si sacaras una espada',
      'Vuelve hasta el estiramiento completo detrás de la cabeza',
    ],
    mistakes: ['Mover el codo durante la extensión', 'Recorrido corto sin estirar el tríceps'],
    trend: {
      by: 'Jeff Nippard',
      why: 'El brazo por encima de la cabeza estira la porción larga del tríceps y la línea cruzada respeta el plano natural del hombro.',
    },
  },
  {
    id: 'smith-jm-press',
    name: 'JM press en multipower',
    aliases: ['JM press', 'JM press Smith', 'Press JM'],
    primary: ['triceps'],
    secondary: ['chest'],
    equipment: 'smith',
    pattern: 'isolation_upper',
    compound: true,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Banco plano, agarre a la anchura de los hombros o algo menos',
      'Baja la barra hacia la barbilla doblando los codos, que avanzan hacia los pies',
      'Antebrazos casi tocando bíceps abajo',
      'Extiende los codos para volver por el mismo camino',
    ],
    mistakes: ['Convertirlo en un press cerrado normal', 'Bajar con prisa y castigar los codos'],
    trend: {
      by: 'JM Blakley; popularizado en multipower por Jeff Nippard',
      why: 'Mezcla press y extensión: carga mucho el tríceps con más peso que un press francés y la guía lo hace estable.',
    },
  },
  {
    id: 'cable-triceps-kickback',
    name: 'Patada de tríceps en polea',
    aliases: ['Cable kickback', 'Patada de tríceps con cable'],
    primary: ['triceps'],
    secondary: [],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 60,
    cues: [
      'Polea baja o media, torso inclinado hacia delante',
      'Brazo pegado al costado y paralelo al suelo',
      'Extiende el codo hasta bloquear y aprieta',
      'Vuelve despacio sin mover el hombro',
    ],
    mistakes: ['Balancear el brazo desde el hombro', 'Dejar caer el codo al volver'],
    trend: {
      by: 'Jeff Nippard',
      why: 'A diferencia de la patada con mancuerna, la polea mantiene la tensión en todo el recorrido y no solo arriba.',
    },
  },
  {
    id: 'cable-press-around',
    name: 'Press-around en polea',
    aliases: ['Press around', 'Cable press-around', 'Press cruzado en polea'],
    primary: ['chest'],
    secondary: ['front_delts', 'triceps'],
    equipment: 'cable',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2.5,
    restSec: 75,
    cues: [
      'De espaldas a una polea a media altura, un solo brazo',
      'Empieza con el codo atrás y el pecho estirado',
      'Empuja en arco por delante del cuerpo hasta cruzar la línea media',
      'Aprieta un segundo al final',
    ],
    mistakes: ['Girar el torso para acompañar', 'Doblar y estirar el codo como un press'],
    trend: {
      by: 'Jeff Nippard',
      why: 'Llevar la mano más allá de la línea media acorta el pectoral al máximo, una contracción que el press normal no alcanza.',
    },
  },
  {
    id: 'helms-row',
    name: 'Remo Helms',
    aliases: ['Helms row', 'Remo con mancuernas apoyado en banco de pie'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'dumbbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'De pie, apoya el pecho en el respaldo de un banco inclinado',
      'Rodillas algo flexionadas y peso en los talones',
      'Rema llevando los codos hacia atrás sin despegar el pecho',
      'Baja hasta estirar del todo la espalda',
    ],
    mistakes: ['Despegar el pecho para hacer trampa', 'Encoger los hombros al tirar'],
    trend: {
      by: 'Eric Helms; difundido por Jeff Nippard',
      why: 'El pecho apoyado elimina el impulso del tronco: toda la carga va a la espalda sin cansar la zona lumbar.',
    },
  },
  {
    id: 'kelso-shrug',
    name: 'Encogimientos Kelso',
    aliases: [
      'Kelso shrug',
      'Encogimiento con pecho apoyado',
      'Retracción escapular con pecho apoyado',
    ],
    primary: ['traps'],
    secondary: ['back', 'rear_delts'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 75,
    cues: [
      'Pecho apoyado en un banco inclinado o en la máquina de remo',
      'Brazos estirados, sin doblar los codos',
      'Junta las escápulas hacia atrás y algo hacia abajo',
      'Mantén un segundo y suelta despacio',
    ],
    mistakes: ['Doblar los codos y convertirlo en remo', 'Encoger hacia las orejas'],
    trend: {
      by: 'Paul Kelso',
      why: 'Retraer las escápulas con el pecho apoyado alinea la tracción con el trapecio medio y los romboides, que el encogimiento normal apenas toca.',
    },
  },
  {
    id: 'kroc-row',
    name: 'Remo Kroc',
    aliases: ['Kroc row', 'Remo pesado con mancuerna a una mano'],
    primary: ['back'],
    secondary: ['traps', 'biceps', 'rear_delts', 'forearms'],
    equipment: 'dumbbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2,
    restSec: 120,
    cues: [
      'Mano libre en el banco o un soporte, pies separados',
      'Mancuerna pesada, series largas de 12–20',
      'Tira hacia la cadera con algo de impulso controlado',
      'Estira del todo abajo en cada repetición',
    ],
    mistakes: ['Convertir el impulso en un giro de tronco', 'Recortar el estiramiento abajo'],
    trend: {
      by: 'Matt Kroc',
      why: 'Mucho peso y muchas repeticiones con un poco de impulso: sobrecarga la espalda alta más que el remo estricto.',
    },
  },
  {
    id: 'seal-row',
    name: 'Remo en banco plano (seal row)',
    aliases: ['Seal row', 'Remo tumbado', 'Remo foca'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'dumbbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Túmbate boca abajo en un banco alto o sobre discos',
      'Brazos colgando y espalda estirada abajo',
      'Rema llevando los codos atrás y abajo',
      'Pecho pegado al banco todo el rato',
    ],
    mistakes: ['Levantar el pecho para ayudarte', 'Banco tan bajo que las pesas tocan el suelo'],
    trend: {
      by: 'John Meadows',
      why: 'Tumbado no hay impulso ni carga lumbar: la espalda trabaja sola y es fácil progresar con peso.',
    },
  },
  {
    id: 'meadows-row',
    name: 'Remo Meadows',
    aliases: ['Meadows row', 'Remo en punta a una mano', 'Remo landmine unilateral'],
    primary: ['back'],
    secondary: ['rear_delts', 'biceps', 'traps'],
    equipment: 'barbell',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2.5,
    restSec: 90,
    cues: [
      'De lado a la barra anclada, agarre prono en el extremo',
      'Cadera alta y atrás, antebrazo libre en la rodilla',
      'Tira con el codo hacia arriba y hacia fuera',
      'Deja que la escápula se estire abajo',
    ],
    mistakes: ['Usar las piernas para levantar la barra', 'Girar el tronco al tirar'],
    trend: {
      by: 'John Meadows',
      why: 'El agarre en el extremo y el codo abierto cargan la espalda alta y el dorsal con mucho estiramiento abajo.',
    },
  },
  {
    id: 'wide-grip-cable-row',
    name: 'Remo en polea con agarre abierto',
    aliases: ['Remo abierto en polea', 'Wide-grip cable row', 'Remo para deltoides posterior'],
    primary: ['back', 'rear_delts'],
    secondary: ['traps', 'biceps'],
    equipment: 'cable',
    pattern: 'horizontal_pull',
    compound: true,
    increment: 2.5,
    restSec: 90,
    cues: [
      'Barra larga, agarre prono más ancho que los hombros',
      'Tira hacia la parte baja del pecho con los codos abiertos',
      'Codos a unos 60–80° del torso',
      'Vuelve estirando la espalda sin balancear',
    ],
    mistakes: [
      'Echar el torso atrás para mover más',
      'Pegar los codos y convertirlo en remo normal',
    ],
    trend: {
      by: 'Jeff Nippard',
      why: 'Con los codos abiertos la línea de tracción se alinea con el deltoides posterior y la espalda alta.',
    },
  },
  {
    id: 'pendulum-squat',
    name: 'Sentadilla péndulo',
    aliases: ['Pendulum squat', 'Sentadilla pendular', 'Hack péndulo'],
    primary: ['quads'],
    secondary: ['glutes', 'adductors'],
    equipment: 'plate_loaded',
    pattern: 'squat',
    compound: true,
    increment: 5,
    restSec: 150,
    cues: [
      'Espalda pegada al respaldo y hombros bajo las almohadillas',
      'Pies a media plataforma, a la anchura de la cadera',
      'Baja todo lo que puedas con las rodillas por delante de los pies',
      'Empuja con todo el pie sin bloquear de golpe',
    ],
    mistakes: ['Recorrido corto por exceso de peso', 'Despegar los talones'],
    trend: {
      by: 'Jeff Nippard',
      why: 'La trayectoria en arco carga el cuádriceps en el fondo, donde está más estirado, con la espalda totalmente apoyada.',
    },
  },
  {
    id: 'sissy-squat',
    name: 'Sentadilla sissy',
    aliases: ['Sissy squat'],
    primary: ['quads'],
    secondary: [],
    equipment: 'bodyweight',
    pattern: 'isolation_lower',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 75,
    cues: [
      'Sujétate a un soporte con una mano',
      'Ponte de puntillas y lleva las rodillas hacia delante',
      'Cadera extendida: rodillas, cadera y hombros en línea',
      'Baja hasta donde controles y sube apretando el cuádriceps',
    ],
    mistakes: ['Doblar la cadera y convertirlo en sentadilla', 'Bajar sin control al principio'],
    trend: {
      by: 'Jeff Nippard y FitnessFAQs',
      why: 'Con la cadera estirada el recto femoral trabaja alargado, algo que ni la sentadilla ni la extensión consiguen.',
    },
  },
  {
    id: 'reverse-nordic',
    name: 'Nórdico inverso',
    aliases: ['Reverse Nordic', 'Curl nórdico invertido', 'Reverse nordic curl'],
    primary: ['quads'],
    secondary: ['abs'],
    equipment: 'bodyweight',
    pattern: 'isolation_lower',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 75,
    cues: [
      'De rodillas sobre una colchoneta, pies sujetos o libres',
      'Cuerpo recto de rodillas a cabeza, glúteos apretados',
      'Inclínate hacia atrás despacio solo hasta donde controles',
      'Vuelve tirando con los cuádriceps',
    ],
    mistakes: ['Doblar la cadera hacia atrás', 'Dejarte caer sin control'],
    trend: {
      by: 'Knees Over Toes Guy y Jeff Nippard',
      why: 'Con la cadera extendida el cuádriceps, sobre todo el recto femoral, se carga muy estirado sin máquina.',
    },
  },
  {
    id: 'nordic-curl',
    name: 'Curl nórdico',
    aliases: ['Nordic curl', 'Nordic hamstring curl', 'Caídas nórdicas'],
    primary: ['hamstrings'],
    secondary: ['glutes', 'calves'],
    equipment: 'bodyweight',
    pattern: 'isolation_lower',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 90,
    cues: [
      'Talones bien sujetos bajo un rodillo o por un compañero',
      'Cuerpo recto de rodillas a cabeza',
      'Baja lo más lento posible frenando con los isquios',
      'Ayúdate con las manos al final y vuelve',
    ],
    mistakes: ['Doblar la cadera para acortar la palanca', 'Caer de golpe en la primera mitad'],
    trend: {
      by: 'Knees Over Toes Guy',
      why: 'Carga excéntrica muy alta sobre los isquios estirados: hipertrofia y menos riesgo de lesiones.',
    },
  },
  {
    id: 'copenhagen-plank',
    name: 'Plancha Copenhague',
    aliases: ['Copenhagen plank', 'Plancha de aductores'],
    primary: ['adductors'],
    secondary: ['abs'],
    equipment: 'bodyweight',
    pattern: 'core',
    timed: true,
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 60,
    cues: [
      'Plancha lateral con la pierna de arriba apoyada en un banco',
      'Rodilla (fácil) o tobillo (difícil) sobre el banco',
      'Eleva la cadera hasta alinear el cuerpo',
      'Mantén el tiempo sin dejar caer la cadera',
    ],
    mistakes: ['Cadera hundida o adelantada', 'Empezar con la palanca larga sin base'],
    trend: {
      by: 'E3 Rehab',
      why: 'Fortalece los aductores en isometría con poca carga, y se asocia a menos lesiones de ingle.',
    },
  },
  {
    id: 'deficit-bulgarian-split-squat',
    name: 'Sentadilla búlgara con déficit',
    aliases: [
      'Búlgara con déficit',
      'Deficit Bulgarian split squat',
      'Búlgara pie delantero elevado',
    ],
    primary: ['quads', 'glutes'],
    secondary: ['adductors', 'hamstrings'],
    equipment: 'dumbbell',
    pattern: 'lunge',
    compound: true,
    increment: 2,
    restSec: 120,
    cues: [
      'Pie delantero sobre un disco o step bajo, el de atrás en el banco',
      'Baja hasta que la rodilla trasera pase la altura del pie delantero',
      'Torso algo inclinado para más glúteo, recto para más cuádriceps',
      'Empuja con todo el pie delantero',
    ],
    mistakes: ['Déficit tan alto que pierdes el equilibrio', 'Rebotar abajo'],
    trend: {
      by: 'Sergio Tejeda',
      why: 'El déficit añade recorrido: glúteo y cuádriceps trabajan más estirados en el fondo.',
    },
  },
  {
    id: 'atg-split-squat',
    name: 'Zancada ATG (rodilla por delante del pie)',
    aliases: ['ATG split squat', 'Split squat ATG', 'Knees over toes split squat'],
    primary: ['quads'],
    secondary: ['glutes', 'adductors'],
    equipment: 'bodyweight',
    pattern: 'lunge',
    compound: true,
    increment: 2,
    bodyweight: true,
    restSec: 90,
    cues: [
      'Paso largo, pie delantero entero en el suelo',
      'Lleva la rodilla delantera por delante de los dedos',
      'Baja hasta que el isquio toque la pantorrilla',
      'Pierna de atrás casi estirada para estirar el flexor de cadera',
    ],
    mistakes: ['Levantar el talón delantero', 'Forzar el rango antes de tenerlo'],
    trend: {
      by: 'Knees Over Toes Guy (Ben Patrick)',
      why: 'Fortalece la rodilla en flexión completa y estira el flexor de cadera de la pierna trasera a la vez.',
    },
  },
  {
    id: 'tibialis-raise',
    name: 'Elevación de tibial',
    aliases: ['Tibialis raise', 'Elevación de tibiales', 'Tib raise'],
    primary: ['calves'],
    secondary: [],
    equipment: 'bodyweight',
    pattern: 'isolation_lower',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 45,
    cues: [
      'Espalda contra la pared, talones algo separados de ella',
      'Sube las puntas de los pies todo lo que puedas',
      'Aguanta un segundo arriba',
      'Baja despacio sin apoyar del todo',
    ],
    mistakes: ['Rebotar las puntas', 'Talones demasiado cerca de la pared'],
    trend: {
      by: 'Knees Over Toes Guy',
      why: 'Trabaja el tibial anterior, el antagonista del gemelo, que casi nadie entrena y ayuda a rodillas y espinillas.',
    },
  },
  {
    id: 'frog-pump',
    name: 'Frog pump',
    aliases: ['Puente de glúteo en rana', 'Frog pumps'],
    primary: ['glutes'],
    secondary: ['adductors'],
    equipment: 'bodyweight',
    pattern: 'hinge',
    compound: false,
    increment: 2.5,
    bodyweight: true,
    restSec: 60,
    cues: [
      'Tumbado boca arriba, plantas de los pies juntas y rodillas abiertas',
      'Talones cerca del glúteo',
      'Empuja la cadera arriba apretando glúteos',
      'Series largas y rápidas, sin arquear la lumbar',
    ],
    mistakes: ['Subir con la lumbar en vez de con los glúteos', 'Pies demasiado lejos'],
    trend: {
      by: 'Bret Contreras',
      why: 'La rotación externa de cadera saca de juego a los isquios y deja todo el trabajo al glúteo.',
    },
  },
  {
    id: 'spanish-squat',
    name: 'Sentadilla española con banda',
    aliases: ['Spanish squat', 'Sentadilla española'],
    primary: ['quads'],
    secondary: ['glutes'],
    equipment: 'band',
    pattern: 'squat',
    compound: true,
    increment: 1,
    restSec: 60,
    cues: [
      'Banda fuerte detrás de las rodillas anclada a un soporte',
      'Echa la cadera atrás con las tibias verticales',
      'Baja a 60–90° de rodilla y mantén',
      'Torso erguido, peso en los talones',
    ],
    mistakes: ['Banda floja que no sujeta', 'Rodillas por delante de los pies'],
    trend: {
      by: 'E3 Rehab',
      why: 'La banda mantiene las tibias verticales: el cuádriceps trabaja en isometría y alivia el tendón rotuliano.',
    },
  },
  {
    id: 'lying-dumbbell-curl',
    name: 'Curl tumbado con mancuernas',
    aliases: ['Lying dumbbell curl', 'Curl en banco plano', 'Curl tumbado'],
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Túmbate en un banco plano alto con los brazos colgando',
      'Brazos por detrás de la línea del cuerpo al empezar',
      'Sube sin adelantar los codos',
      'Baja despacio hasta el estiramiento completo',
    ],
    mistakes: ['Adelantar los codos al subir', 'Mucho peso que fuerza el hombro abajo'],
    trend: {
      by: 'Jeremy Ethier y Jeff Nippard',
      why: 'Con el brazo detrás del cuerpo el bíceps empieza muy estirado y la carga es máxima justo ahí.',
    },
  },
  {
    id: 'inverse-zottman-curl',
    name: 'Curl Zottman inverso',
    aliases: ['Inverse Zottman curl', 'Zottman invertido'],
    primary: ['biceps', 'forearms'],
    secondary: [],
    equipment: 'dumbbell',
    pattern: 'isolation_upper',
    compound: false,
    increment: 2,
    restSec: 60,
    cues: [
      'Sube en agarre martillo, con los pulgares arriba',
      'Arriba gira las palmas hacia ti',
      'Baja despacio en supinación',
      'Codos quietos junto al costado',
    ],
    mistakes: ['Bajar rápido y perder la excéntrica', 'Balancear el torso'],
    trend: {
      by: 'Jeff Nippard',
      why: 'Subes fuerte en martillo y bajas en supino: la excéntrica, más intensa, carga el bíceps en su posición fuerte.',
    },
  },
  {
    id: 'b-stance-rdl',
    name: 'Peso muerto rumano B-stance',
    aliases: ['B-stance RDL', 'Kickstand RDL', 'Rumano con pie de apoyo'],
    primary: ['hamstrings', 'glutes'],
    secondary: ['lower_back'],
    equipment: 'dumbbell',
    pattern: 'hinge',
    compound: true,
    increment: 2,
    restSec: 90,
    cues: [
      'Pie trasero con la punta a la altura del talón delantero',
      'El 80–90 % del peso en la pierna de delante',
      'Cadera atrás con la espalda neutra',
      'Sube apretando el glúteo de la pierna delantera',
    ],
    mistakes: ['Repartir el peso al 50 %', 'Redondear la espalda abajo'],
    trend: {
      by: 'Tony Gentilcore',
      why: 'Casi unilateral sin el problema del equilibrio: cargas más que en el rumano a una pierna y corriges asimetrías.',
    },
  },

  /* ───────────── Cardio ───────────── */
  {
    id: 'treadmill',
    name: 'Cinta de correr',
    aliases: ['Cinta', 'Correr', 'Caminar en cinta inclinada'],
    primary: [],
    secondary: [],
    equipment: 'cardio',
    pattern: 'cardio',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Calienta 3–5 minutos a ritmo suave',
      'No te agarres a las barras si caminas inclinado',
      'Zancada corta y cadencia alta al correr',
    ],
    mistakes: ['Agarrarse a la barandilla', 'Empezar demasiado rápido'],
  },
  {
    id: 'elliptical',
    name: 'Elíptica',
    aliases: ['Bicicleta elíptica'],
    primary: [],
    secondary: [],
    equipment: 'cardio',
    pattern: 'cardio',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Torso erguido y abdomen activo',
      'Empuja también con los brazos',
      'Apoya todo el pie en los pedales',
    ],
    mistakes: ['Apoyarse sobre las manillas', 'Resistencia tan baja que no hay esfuerzo'],
  },
  {
    id: 'stationary-bike',
    name: 'Bicicleta estática',
    aliases: ['Bici estática', 'Spinning'],
    primary: [],
    secondary: [],
    equipment: 'cardio',
    pattern: 'cardio',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Ajusta el sillín: rodilla casi extendida abajo',
      'Cadencia constante',
      'Hombros relajados',
    ],
    mistakes: ['Sillín demasiado bajo', 'Balancear la cadera'],
  },
  {
    id: 'rowing-machine',
    name: 'Remo ergómetro',
    aliases: ['Máquina de remo', 'Remoergómetro'],
    primary: [],
    secondary: [],
    equipment: 'cardio',
    pattern: 'cardio',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Orden al tirar: piernas, cadera, brazos',
      'Orden al volver: brazos, cadera, piernas',
      'Espalda neutra todo el recorrido',
    ],
    mistakes: ['Tirar con los brazos antes de empujar con las piernas', 'Encorvar la espalda'],
  },
  {
    id: 'stair-climber',
    name: 'Escaleras',
    aliases: ['Stair climber', 'Escaladora', 'Máquina de escaleras'],
    primary: [],
    secondary: [],
    equipment: 'cardio',
    pattern: 'cardio',
    compound: false,
    increment: 1,
    restSec: 60,
    cues: [
      'Torso erguido',
      'Apoya todo el pie en cada escalón',
      'Agarra la barandilla solo para equilibrarte',
    ],
    mistakes: ['Cargar el peso en las manos', 'Ir de puntillas'],
  },
]

export const EXERCISES: readonly Exercise[] = DEFS.map(def)

const BY_ID = new Map(EXERCISES.map((e) => [e.id, e]))

/**
 * Exercises with a `trend`, for the library's «Novedades» section: the dedicated block first
 * (it opens with the lat pull-around), then older catalog entries that later got a `trend`.
 */
export const TRENDING: readonly Exercise[] = (() => {
  const t = EXERCISES.filter((e) => e.trend)
  const start = t.findIndex((e) => e.id === 'cable-lat-pull-around')
  return start > 0 ? [...t.slice(start), ...t.slice(0, start)] : t
})()

export function exerciseById(id: string): Exercise | undefined {
  return BY_ID.get(id)
}

/** Lowercase and strip diacritics so "jalon" matches "Jalón". */
export function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

const SEARCH_INDEX = EXERCISES.map((e) => ({
  e,
  name: normalize(e.name),
  terms: [e.name, ...(e.aliases ?? [])].map(normalize),
}))

/**
 * Accent-insensitive search by name and aliases. Every query word must appear in some term.
 * Results: name prefix matches first, then name matches, then alias matches.
 */
export function searchExercises(query: string): Exercise[] {
  const q = normalize(query)
  if (!q) return [...EXERCISES]
  const words = q.split(/\s+/)
  const scored: { e: Exercise; score: number }[] = []
  for (const { e, name, terms } of SEARCH_INDEX) {
    const hit = terms.some((t) => words.every((w) => t.includes(w)))
    if (!hit) continue
    const score = name.startsWith(q) ? 0 : words.every((w) => name.includes(w)) ? 1 : 2
    scored.push({ e, score })
  }
  return scored
    .sort(
      (a, b) =>
        a.score - b.score ||
        a.e.name.length - b.e.name.length ||
        a.e.name.localeCompare(b.e.name, 'es'),
    )
    .map((s) => s.e)
}
